import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  FileText,
  Calendar,
  Download,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import ApiService from '../services/ApiService';
import { useXeroConnection } from '../contexts/XeroConnectionContext';

const FinancialReports = () => {
  const { isConnected } = useXeroConnection();
  const [activeReport, setActiveReport] = useState('profit-loss');
  const [invoiceFilters, setInvoiceFilters] = useState(new Set()); // empty = show all
  const [reportData, setReportData] = useState(null);
  const [summaryMetrics, setSummaryMetrics] = useState(null);
  const [priorYearData, setPriorYearData] = useState(null);
  const [currentYearQuarters, setCurrentYearQuarters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheInfo, setCacheInfo] = useState(null); // Track cache timestamp and source
  const [snapshotInfo, setSnapshotInfo] = useState(null); // { fetchedAt: Date } when showing org snapshot
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
      fromDate: `${now.getFullYear()}-01-01`,
      toDate: localDate,
      asOfDate: localDate
    };
  });

  const reports = [
    { id: 'profit-loss', name: 'Profit & Loss', icon: TrendingUp, description: 'Income statement showing revenue and expenses' },
    { id: 'balance-sheet', name: 'Balance Sheet', icon: BarChart3, description: 'Financial position at a specific date' },
    { id: 'trial-balance', name: 'Trial Balance', icon: FileText, description: 'List of all accounts with balances' },
    { id: 'cash-flow-aging', name: 'Cash Flow Aging', icon: Clock, description: 'Accounts receivable aging by client' }
  ];

  useEffect(() => {
    console.log(`📊 Loading ${activeReport} report...`);
    loadReport();
    if (activeReport === 'profit-loss') {
      console.log(`📅 Loading prior year data for Profit & Loss...`);
      loadPriorYearData();
      loadCurrentYearQuarters();
    }
  }, [activeReport, dateRange]);

  const loadPriorYearData = async () => {
    // Only load for current year Profit & Loss
    if (activeReport !== 'profit-loss') return;
    
    const currentYear = new Date().getFullYear();
    const priorYear = currentYear - 1;
    const cacheKey = `prior_year_data_${priorYear}`;
    
    try {
      console.log(`📅 Loading quarterly data for projection analysis...`);
      
      // Fetch all 4 quarters from prior year
      const quarters = [
        { name: 'Q1', from: `${priorYear}-01-01`, to: `${priorYear}-03-31` },
        { name: 'Q2', from: `${priorYear}-04-01`, to: `${priorYear}-06-30` },
        { name: 'Q3', from: `${priorYear}-07-01`, to: `${priorYear}-09-30` },
        { name: 'Q4', from: `${priorYear}-10-01`, to: `${priorYear}-12-31` }
      ];
      
      const priorYearQuarters = [];
      
      // Fetch each quarter from prior year (cached on server for 5 days)
      for (const quarter of quarters) {
        try {
          const data = await ApiService.getXeroProfitAndLoss(quarter.from, quarter.to);
          if (data && !data.error) {
            const processed = processReportData(data);
            priorYearQuarters.push({
              quarter: quarter.name,
              ...processed.summaryMetrics,
              dateRange: `${quarter.from} to ${quarter.to}`
            });
            console.log(`✅ Loaded ${priorYear} ${quarter.name}:`, formatCurrency(processed.summaryMetrics.totalRevenue));
          }
        } catch (err) {
          console.warn(`⚠️ Could not load ${priorYear} ${quarter.name}:`, err);
        }
      }
      
      // If we got any quarterly data, use it
      if (priorYearQuarters.length > 0) {
        // Build prior year data object with quarterly data
        const priorData = {
          quarters: priorYearQuarters,
          fullYear: priorYearQuarters.reduce((sum, q) => sum + q.totalRevenue, 0),
          fullYearProfit: priorYearQuarters.reduce((sum, q) => sum + q.coreProfit, 0)
        };
        
        // Also get same period from last year for direct comparison (optional enhancement)
        try {
          const priorFromDate = dateRange.fromDate.replace(currentYear.toString(), priorYear.toString());
          const priorToDate = dateRange.toDate.replace(currentYear.toString(), priorYear.toString());
          const samePeriodData = await ApiService.getXeroProfitAndLoss(priorFromDate, priorToDate);
          
          if (samePeriodData && !samePeriodData.error) {
            const processed = processReportData(samePeriodData);
            priorData.samePeriod = processed.summaryMetrics;
            console.log('✅ Prior year same period comparison loaded');
          }
        } catch (err) {
          console.warn('⚠️ Could not load same period comparison (optional):', err);
        }
        
        // Save to cache for future use
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            data: priorData,
            timestamp: new Date().toISOString(),
            year: priorYear
          }));
          console.log('💾 Saved prior year data to cache');
        } catch (cacheErr) {
          console.warn('Failed to cache prior year data:', cacheErr);
        }
        
        // Set prior year data (with or without same period comparison)
        setPriorYearData(priorData);
        console.log('✅ Prior year quarterly analysis complete');
      } else {
        // No quarterly data loaded, try cache as fallback
        throw new Error('No quarterly data available from API');
      }
    } catch (error) {
      console.warn('⚠️ Could not load prior year data from API:', error);
      
      // Try to load from cache as fallback
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const cachedData = JSON.parse(cached);
          setPriorYearData(cachedData.data);
          const ageMinutes = Math.round((new Date() - new Date(cachedData.timestamp)) / (1000 * 60));
          console.log(`📁 Using cached prior year data from ${priorYear} (${ageMinutes} minutes old)`);
          console.log('✅ Cached prior year data loaded for business insights');
          return;
        }
      } catch (cacheErr) {
        console.warn('Failed to load cached prior year data:', cacheErr);
      }
      
      // No API data and no cache - use null (will fall back to linear)
      setPriorYearData(null);
      console.log('⚠️ No prior year data available - business insights will use linear projections');
    }
  };

  const loadCurrentYearQuarters = async () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const toDateStr = dateRange.toDate;
    const fromDateStr = dateRange.fromDate;

    const allQuarters = [
      { name: 'Q1', from: `${currentYear}-01-01`, to: `${currentYear}-03-31` },
      { name: 'Q2', from: `${currentYear}-04-01`, to: `${currentYear}-06-30` },
      { name: 'Q3', from: `${currentYear}-07-01`, to: `${currentYear}-09-30` },
      { name: 'Q4', from: `${currentYear}-10-01`, to: `${currentYear}-12-31` },
    ];

    // Only fetch quarters that overlap with the selected date range
    const relevantQuarters = allQuarters.filter(q => q.from <= toDateStr && q.to >= fromDateStr);

    const results = [];
    for (const q of relevantQuarters) {
      // Clamp the quarter's range to the selected date range
      const effectiveTo = q.to < toDateStr ? q.to : toDateStr;
      const effectiveFrom = q.from > fromDateStr ? q.from : fromDateStr;
      const isPartial = effectiveTo !== q.to;
      try {
        const data = await ApiService.getXeroProfitAndLoss(effectiveFrom, effectiveTo);
        if (data && !data.error) {
          const processed = processReportData(data);
          if (processed.summaryMetrics) {
            results.push({ name: q.name, isPartial, ...processed.summaryMetrics });
          }
        }
      } catch (err) {
        console.warn(`⚠️ Could not load ${currentYear} ${q.name}:`, err);
      }
    }
    setCurrentYearQuarters(results);
  };

  const getCacheKey = (reportType, dateRange) => {
    switch (reportType) {
      case 'profit-loss':
        return `xero_report_${reportType}_${dateRange.fromDate}_${dateRange.toDate}`;
      case 'balance-sheet':
      case 'trial-balance':
        return `xero_report_${reportType}_${dateRange.asOfDate}`;
      default:
        return `xero_report_${reportType}`;
    }
  };

  const saveReportToCache = (reportType, dateRange, data) => {
    try {
      const cacheKey = getCacheKey(reportType, dateRange);
      const cacheData = {
        data: data,
        timestamp: new Date().toISOString(),
        reportType: reportType,
        dateRange: dateRange
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log('💾 Saved report to cache:', cacheKey);
    } catch (error) {
      console.warn('Failed to save report to cache:', error);
    }
  };

  const loadReportFromCache = (reportType, dateRange) => {
    try {
      const cacheKey = getCacheKey(reportType, dateRange);
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const cacheData = JSON.parse(cached);
        console.log('📁 Loaded report from cache:', cacheKey, 'Age:',
          Math.round((new Date() - new Date(cacheData.timestamp)) / (1000 * 60)), 'minutes');
        return cacheData;
      }
    } catch (error) {
      console.warn('Failed to load report from cache:', error);
    }
    return null;
  };

  // Scan all localStorage keys for the most recently saved report of this type
  const findMostRecentCachedReport = (reportType) => {
    try {
      const prefix = `xero_report_${reportType}_`;
      let newest = null;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
          try {
            const item = JSON.parse(localStorage.getItem(key));
            if (item && item.timestamp) {
              if (!newest || new Date(item.timestamp) > new Date(newest.timestamp)) {
                newest = item;
              }
            }
          } catch (e) { /* skip corrupt entries */ }
        }
      }
      if (newest) {
        console.log('📁 Most recent cached report found for', reportType, '— age:',
          Math.round((new Date() - new Date(newest.timestamp)) / (1000 * 60)), 'minutes');
      }
      return newest;
    } catch (e) {
      return null;
    }
  };

  // Helper: apply report data to state regardless of source
  const applyReportData = (data, source, snapshotFetchedAt = null) => {
    if (activeReport === 'profit-loss') {
      const processedData = processReportData(data);
      setReportData(processedData.reportData);
      setSummaryMetrics(processedData.summaryMetrics);
    } else {
      setReportData(data);
      setSummaryMetrics(null);
    }
    setCacheInfo({ timestamp: new Date().toISOString(), source });
    setSnapshotInfo(snapshotFetchedAt ? { fetchedAt: snapshotFetchedAt } : null);
  };

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    setCacheInfo(null);
    setSnapshotInfo(null);

    // ── 1. If connected to Xero, try the live API first ──────────────────────
    if (isConnected) {
      try {
        let data;
        switch (activeReport) {
          case 'profit-loss':
            data = await ApiService.getXeroProfitAndLoss(dateRange.fromDate, dateRange.toDate);
            break;
          case 'balance-sheet':
            data = await ApiService.getXeroBalanceSheet(dateRange.asOfDate);
            break;
          case 'trial-balance':
            data = await ApiService.getXeroTrialBalance(dateRange.asOfDate);
            break;
          case 'cash-flow-aging':
            data = await ApiService.getXeroCashFlowAging();
            break;
          default:
            throw new Error('Unknown report type');
        }
        if (data && !data.error) {
          saveReportToCache(activeReport, dateRange, data);
          applyReportData(data, 'live');
          setLoading(false);
          return;
        }
        throw new Error(data?.error || 'API returned error');
      } catch (liveError) {
        console.warn('⚠️ Live Xero API failed, falling back:', liveError.message);
      }
    }

    // ── 2. Try browser localStorage (per-user cache, exact date range) ───────
    const cachedReport = loadReportFromCache(activeReport, dateRange);
    if (cachedReport) {
      console.log('💾 Using browser-cached report data');
      applyReportData(cachedReport.data, 'cache');
      setLoading(false);
      return;
    }

    // ── 2b. Not connected: scan localStorage for any cached version ───────────
    if (!isConnected) {
      const recentCache = findMostRecentCachedReport(activeReport);
      if (recentCache) {
        applyReportData(recentCache.data, 'cache');
        setLoading(false);
        return;
      }
    }

    // ── 3. Try org-wide file snapshot (server-persisted, no Xero needed) ─────
    try {
      const snapshot = await ApiService.getReportSnapshot(activeReport);
      if (snapshot && !snapshot.error && snapshot.data) {
        console.log('📋 Using org snapshot from', snapshot.fetchedAt);
        applyReportData(snapshot.data, 'snapshot', new Date(snapshot.fetchedAt));
        setLoading(false);
        return;
      }
    } catch (snapshotError) {
      console.warn('📋 No org snapshot available:', snapshotError.message);
    }

    // ── 4. Nothing available ──────────────────────────────────────────────────
    setError(isConnected
      ? 'Unable to load report data. Please try refreshing.'
      : 'Not connected to Xero and no org snapshot is available yet. A connected user must load this report first.');
    setReportData(null);
    setSummaryMetrics(null);
    setLoading(false);
  };

  const getMockReportData = (reportType) => {
    switch (reportType) {
      case 'profit-loss':
        return {
          Reports: [{
            ReportName: "Profit and Loss",
            ReportDate: new Date().toISOString().split('T')[0],
            Rows: [
              {
                RowType: "Section",
                Title: "Income",
                Rows: [
                  { RowType: "Row", Cells: [{ Value: "Revenue" }, { Value: "15,750.00" }] },
                  { RowType: "Row", Cells: [{ Value: "Other Income" }, { Value: "250.00" }] },
                  { RowType: "SummaryRow", Cells: [{ Value: "Total Income" }, { Value: "16,000.00" }] }
                ]
              },
              {
                RowType: "Section", 
                Title: "Cost of Sales",
                Rows: [
                  { RowType: "Row", Cells: [{ Value: "Contractor Costs" }, { Value: "8,500.00" }] },
                  { RowType: "Row", Cells: [{ Value: "Materials" }, { Value: "1,200.00" }] },
                  { RowType: "SummaryRow", Cells: [{ Value: "Total Cost of Sales" }, { Value: "9,700.00" }] }
                ]
              },
              {
                RowType: "Section",
                Title: "Operating Expenses", 
                Rows: [
                  { RowType: "Row", Cells: [{ Value: "Insurance" }, { Value: "800.00" }] },
                  { RowType: "Row", Cells: [{ Value: "Vehicle Expenses" }, { Value: "650.00" }] },
                  { RowType: "Row", Cells: [{ Value: "Office Expenses" }, { Value: "300.00" }] },
                  { RowType: "SummaryRow", Cells: [{ Value: "Total Operating Expenses" }, { Value: "1,750.00" }] }
                ]
              },
              { RowType: "SummaryRow", Cells: [{ Value: "Net Profit" }, { Value: "4,550.00" }] }
            ]
          }]
        };
      case 'balance-sheet':
        return {
          Reports: [{
            ReportName: "Balance Sheet",
            ReportDate: new Date().toISOString().split('T')[0],
            Rows: [
              {
                RowType: "Section",
                Title: "Assets",
                Rows: [
                  {
                    RowType: "Section",
                    Title: "Current Assets",
                    Rows: [
                      { RowType: "Row", Cells: [{ Value: "Cash at Bank" }, { Value: "12,500.00" }] },
                      { RowType: "Row", Cells: [{ Value: "Accounts Receivable" }, { Value: "8,750.00" }] },
                      { RowType: "SummaryRow", Cells: [{ Value: "Total Current Assets" }, { Value: "21,250.00" }] }
                    ]
                  },
                  {
                    RowType: "Section", 
                    Title: "Non-Current Assets",
                    Rows: [
                      { RowType: "Row", Cells: [{ Value: "Equipment" }, { Value: "15,000.00" }] },
                      { RowType: "Row", Cells: [{ Value: "Vehicles" }, { Value: "25,000.00" }] },
                      { RowType: "SummaryRow", Cells: [{ Value: "Total Non-Current Assets" }, { Value: "40,000.00" }] }
                    ]
                  },
                  { RowType: "SummaryRow", Cells: [{ Value: "Total Assets" }, { Value: "61,250.00" }] }
                ]
              },
              {
                RowType: "Section",
                Title: "Liabilities",
                Rows: [
                  {
                    RowType: "Section",
                    Title: "Current Liabilities", 
                    Rows: [
                      { RowType: "Row", Cells: [{ Value: "Accounts Payable" }, { Value: "3,200.00" }] },
                      { RowType: "Row", Cells: [{ Value: "Accrued Expenses" }, { Value: "1,500.00" }] },
                      { RowType: "SummaryRow", Cells: [{ Value: "Total Current Liabilities" }, { Value: "4,700.00" }] }
                    ]
                  },
                  { RowType: "SummaryRow", Cells: [{ Value: "Total Liabilities" }, { Value: "4,700.00" }] }
                ]
              },
              {
                RowType: "Section",
                Title: "Equity",
                Rows: [
                  { RowType: "Row", Cells: [{ Value: "Owner's Equity" }, { Value: "56,550.00" }] },
                  { RowType: "SummaryRow", Cells: [{ Value: "Total Equity" }, { Value: "56,550.00" }] }
                ]
              }
            ]
          }]
        };
      case 'trial-balance':
        return {
          Reports: [{
            ReportName: "Trial Balance",
            ReportDate: new Date().toISOString().split('T')[0],
            Rows: [
              { RowType: "Row", Cells: [{ Value: "Cash at Bank" }, { Value: "12,500.00" }, { Value: "" }] },
              { RowType: "Row", Cells: [{ Value: "Accounts Receivable" }, { Value: "8,750.00" }, { Value: "" }] },
              { RowType: "Row", Cells: [{ Value: "Equipment" }, { Value: "15,000.00" }, { Value: "" }] },
              { RowType: "Row", Cells: [{ Value: "Vehicles" }, { Value: "25,000.00" }, { Value: "" }] },
              { RowType: "Row", Cells: [{ Value: "Accounts Payable" }, { Value: "" }, { Value: "3,200.00" }] },
              { RowType: "Row", Cells: [{ Value: "Owner's Equity" }, { Value: "" }, { Value: "56,550.00" }] },
              { RowType: "Row", Cells: [{ Value: "Revenue" }, { Value: "" }, { Value: "15,750.00" }] },
              { RowType: "Row", Cells: [{ Value: "Contractor Costs" }, { Value: "8,500.00" }, { Value: "" }] },
              { RowType: "Row", Cells: [{ Value: "Operating Expenses" }, { Value: "1,750.00" }, { Value: "" }] },
              { RowType: "SummaryRow", Cells: [{ Value: "TOTAL" }, { Value: "71,500.00" }, { Value: "71,500.00" }] }
            ]
          }]
        };
      default:
        return null;
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const processReportData = (reportData) => {
    if (!reportData?.Reports?.[0]?.Rows) return { reportData, summaryMetrics: null };

    const report = reportData.Reports[0];
    
    // 🔍 DEBUG: Log the full report structure
    console.log('📊 FULL XERO REPORT JSON:', JSON.stringify(reportData, null, 2));
    console.log('📋 REPORT ROWS:', report.Rows);
    
    let abConsultingAmount = 0;

    // Extract AB Consulting amount but KEEP it in the report
    report.Rows.forEach(row => {
      if (row.RowType === 'Section') {
        row.Rows?.forEach(subRow => {
          if (subRow.RowType === 'Row' && 
              subRow.Cells?.[0]?.Value?.toLowerCase().includes('ab consulting')) {
            // Extract AB Consulting amount for calculations (but keep in report)
            const amountStr = subRow.Cells?.[1]?.Value || '0';
            abConsultingAmount += parseFloat(amountStr.replace(/[$,]/g, '')) || 0;
          }
        });
      }
    });

    // Calculate metrics for summary boxes
    const totalRevenue = getTotalRevenueFromRows(report.Rows);
    const totalExpenses = getTotalExpensesFromRows(report.Rows); // All expenses
    const incomeTaxExpense = getIncomeTaxFromRows(report.Rows); // Lump-sum, once per quarter
    const coreOperatingExpenses = totalExpenses - Math.abs(abConsultingAmount); // Exclude AB Consulting
    const coreOperatingExpensesExTax = coreOperatingExpenses - incomeTaxExpense; // Variable expenses only
    const coreProfit = totalRevenue - coreOperatingExpenses; // Revenue - Core Expenses
    const realProfit = getNetProfitFromRows(report.Rows); // Actual net profit from report

    const summaryMetrics = {
      totalRevenue: totalRevenue,
      coreOperatingExpenses: coreOperatingExpenses,
      coreOperatingExpensesExTax: coreOperatingExpensesExTax,
      incomeTaxExpense: incomeTaxExpense,
      coreProfit: coreProfit,
      realProfit: realProfit,
      abConsultingAmount: abConsultingAmount,
      coreMargin: totalRevenue > 0 ? (coreProfit / totalRevenue) * 100 : 0,
      realMargin: totalRevenue > 0 ? (realProfit / totalRevenue) * 100 : 0,
      abConsultingImpact: totalRevenue > 0 ? (Math.abs(abConsultingAmount) / totalRevenue) * 100 : 0
    };

    return {
      reportData: reportData, // Keep original report unchanged
      summaryMetrics: summaryMetrics
    };
  };

  const getTotalRevenueFromRows = (rows) => {
    for (const row of rows) {
      if (row.RowType === 'Section' && 
          (row.Title?.toLowerCase().includes('income') || row.Title?.toLowerCase().includes('revenue'))) {
        for (const subRow of row.Rows || []) {
          if (subRow.RowType === 'SummaryRow' && subRow.Cells?.[0]?.Value?.toLowerCase().includes('total')) {
            return parseFloat(subRow.Cells?.[1]?.Value?.replace(/[$,]/g, '') || '0') || 0;
          }
        }
      }
    }
    return 0;
  };

  const getTotalExpensesFromRows = (rows) => {
    let totalExpenses = 0;
    for (const row of rows) {
      if (row.RowType === 'Section' && 
          (row.Title?.toLowerCase().includes('expense') || row.Title?.toLowerCase().includes('cost'))) {
        for (const subRow of row.Rows || []) {
          if (subRow.RowType === 'Row' && subRow.Cells?.[1]?.Value) {
            const amount = parseFloat(subRow.Cells?.[1]?.Value?.replace(/[$,]/g, '') || '0') || 0;
            totalExpenses += Math.abs(amount);
          }
        }
      }
    }
    return totalExpenses;
  };

  const getIncomeTaxFromRows = (rows) => {
    for (const row of rows) {
      if (row.RowType === 'Section' &&
          (row.Title?.toLowerCase().includes('expense') || row.Title?.toLowerCase().includes('cost'))) {
        for (const subRow of row.Rows || []) {
          if (subRow.RowType === 'Row' &&
              subRow.Cells?.[0]?.Value?.toLowerCase().includes('income tax')) {
            return Math.abs(parseFloat(subRow.Cells?.[1]?.Value?.replace(/[$,]/g, '') || '0') || 0);
          }
        }
      }
    }
    return 0;
  };

  const getNetProfitFromRows = (rows) => {
    console.log('🔍 SEARCHING FOR NET PROFIT...');
    console.log('📋 Total rows to search:', rows.length);
    
    // Search for net profit in priority order (most specific first)
    const searchTerms = [
      { term: 'total comprehensive income', exact: true },
      { term: 'net income', exact: false, exclude: 'before' }, // Match "Net Income" but not "Net Income before Tax"
      { term: 'net profit', exact: false, exclude: 'before' }
    ];
    
    // First pass: Look for exact matches at the top level (after all sections)
    console.log('🔍 PASS 1: Searching top-level rows...');
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.RowType === 'SummaryRow' || row.RowType === 'Row') {
        const cellValue = row.Cells?.[0]?.Value?.toLowerCase() || '';
        const cellAmount = row.Cells?.[1]?.Value || '';
        
        console.log(`  Row ${i}: Type="${row.RowType}", Label="${row.Cells?.[0]?.Value}", Amount="${cellAmount}"`);
        
        for (const searchItem of searchTerms) {
          const matches = cellValue.includes(searchItem.term);
          const hasExclusion = searchItem.exclude && cellValue.includes(searchItem.exclude);
          
          if (matches && !hasExclusion) {
            const amount = parseFloat(cellAmount.replace(/[$,]/g, '') || '0') || 0;
            console.log(`✅ FOUND "${searchItem.term}" at top level: $${amount}`);
            console.log(`   Full row:`, row);
            return amount;
          } else if (matches && hasExclusion) {
            console.log(`   ⏭️ Skipped (contains "${searchItem.exclude}"): "${row.Cells?.[0]?.Value}"`);
          }
        }
      }
    }
    
    // Second pass: Search within sections (less preferred)
    console.log('🔍 PASS 2: Searching within sections...');
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      if (row.RowType === 'Section' && row.Rows) {
        console.log(`  Section ${i}: "${row.Title}" (${row.Rows.length} sub-rows)`);
        
        for (let j = 0; j < row.Rows.length; j++) {
          const subRow = row.Rows[j];
          if (subRow.RowType === 'SummaryRow' || subRow.RowType === 'Row') {
            const cellValue = subRow.Cells?.[0]?.Value?.toLowerCase() || '';
            const cellAmount = subRow.Cells?.[1]?.Value || '';
            
            console.log(`    SubRow ${j}: Type="${subRow.RowType}", Label="${subRow.Cells?.[0]?.Value}", Amount="${cellAmount}"`);
            
            for (const searchItem of searchTerms) {
              const matches = cellValue.includes(searchItem.term);
              const hasExclusion = searchItem.exclude && cellValue.includes(searchItem.exclude);
              
              if (matches && !hasExclusion) {
                const amount = parseFloat(cellAmount.replace(/[$,]/g, '') || '0') || 0;
                console.log(`✅ FOUND "${searchItem.term}" in section: $${amount}`);
                console.log(`   Full subRow:`, subRow);
                return amount;
              } else if (matches && hasExclusion) {
                console.log(`     ⏭️ Skipped (contains "${searchItem.exclude}"): "${subRow.Cells?.[0]?.Value}"`);
              }
            }
          }
        }
      }
    }
    
    console.warn('❌ Net profit not found in report!');
    console.log('📋 Available top-level rows:', rows.map(r => r.Cells?.[0]?.Value || r.Title).join(', '));
    return 0;
  };

  const getBusinessInsight = (coreMargin, abImpact) => {
    if (coreMargin > 30) {
      return `Excellent! ${coreMargin.toFixed(1)}% margin shows strong property preservation operations`;
    } else if (coreMargin > 20) {
      return `Good performance at ${coreMargin.toFixed(1)}% margin. Property business is profitable`;
    } else if (coreMargin > 10) {
      return `Moderate ${coreMargin.toFixed(1)}% margin. Consider optimizing contractor costs`;
    } else if (coreMargin > 0) {
      return `Low ${coreMargin.toFixed(1)}% margin. Review pricing and cost structure`;
    } else {
      return `Core business showing loss. Immediate attention needed on operations`;
    }
  };

  const renderReportTable = (report) => {
    if (!report || !report.Rows) return null;

    // Format the date properly - force local interpretation to avoid UTC midnight offset
    const formatReportDate = (dateStr) => {
      if (!dateStr) return 'Unknown Date';
      try {
        // ISO date-only strings (YYYY-MM-DD) are parsed as UTC midnight by new Date(),
        // which shifts the date backward for UTC- timezones. Append time to keep it local.
        const normalized = /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T00:00:00` : dateStr;
        const date = new Date(normalized);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        return dateStr;
      }
    };

    // Determine if this is a balance sheet to show proper headers
    const isBalanceSheet = activeReport === 'balance-sheet';
    const isTrialBalance = activeReport === 'trial-balance';

    return (
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{report.ReportName}</h3>
              <p className="text-sm text-gray-500">
                {activeReport === 'profit-loss' 
                  ? `For the period ${formatReportDate(dateRange.fromDate)} - ${formatReportDate(dateRange.toDate)}`
                  : `As of ${formatReportDate(report.ReportDate)}`
                }
              </p>
            </div>
            {cacheInfo && (
              <div className={`text-xs px-3 py-1 rounded-full ${
                cacheInfo.source === 'live'
                  ? 'bg-green-100 text-green-800'
                  : cacheInfo.source === 'snapshot'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-yellow-100 text-yellow-800'
              }`}>
                {cacheInfo.source === 'live' ? (
                  <span>Live from Xero</span>
                ) : cacheInfo.source === 'snapshot' ? (
                  <span>Org snapshot &mdash; {snapshotInfo?.fetchedAt?.toLocaleString()}</span>
                ) : (
                  <span>Cached ({Math.round((new Date() - new Date(cacheInfo.timestamp)) / (1000 * 60))} min old)</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            {/* Add proper headers for Balance Sheet and Trial Balance */}
            {(isBalanceSheet || isTrialBalance) && (
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                  </th>
                  {isTrialBalance ? (
                    <>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Debit
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Credit
                      </th>
                    </>
                  ) : (
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                  )}
                </tr>
              </thead>
            )}
            <tbody className="bg-white divide-y divide-gray-200">
              {report.Rows.map((row, index) => renderRow(row, index))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderRow = (row, index) => {
    // Skip Xero's Header rows - they contain date strings that get misformatted as currency
    if (row.RowType === 'Header') return null;

    const isBalanceSheet = activeReport === 'balance-sheet';
    const isTrialBalance = activeReport === 'trial-balance';
    
    // Determine the number of columns based on report type
    const getColSpan = () => {
      if (isTrialBalance) return "3"; // Account, Debit, Credit
      if (isBalanceSheet) return "2"; // Account, Amount
      return "3"; // Default for P&L
    };

    if (row.RowType === 'Section') {
      return (
        <React.Fragment key={index}>
          <tr className="bg-gray-50">
            <td colSpan={getColSpan()} className="px-6 py-3 text-left text-sm font-semibold text-gray-900 uppercase tracking-wider">
              {row.Title}
            </td>
          </tr>
          {row.Rows && row.Rows.map((subRow, subIndex) => renderRow(subRow, `${index}-${subIndex}`))}
        </React.Fragment>
      );
    }

    if (row.RowType === 'SummaryRow') {
      return (
        <tr key={index} className="bg-gray-100 font-semibold border-t-2 border-gray-300">
          <td className="px-6 py-3 text-sm text-gray-900 font-bold">{row.Cells[0]?.Value}</td>
          {isTrialBalance ? (
            <>
              <td className="px-6 py-3 text-sm text-right text-gray-900 font-bold">
                {row.Cells[1]?.Value && formatCurrency(row.Cells[1].Value)}
              </td>
              <td className="px-6 py-3 text-sm text-right text-gray-900 font-bold">
                {row.Cells[2]?.Value && formatCurrency(row.Cells[2].Value)}
              </td>
            </>
          ) : (
            <td className="px-6 py-3 text-sm text-right text-gray-900 font-bold">
              {row.Cells[1]?.Value && formatCurrency(row.Cells[1].Value)}
            </td>
          )}
        </tr>
      );
    }

    return (
      <tr key={index} className="hover:bg-gray-50">
        <td className="px-6 py-3 text-sm text-gray-900 pl-8">{row.Cells[0]?.Value}</td>
        {isTrialBalance ? (
          <>
            <td className="px-6 py-3 text-sm text-right text-gray-900">
              {row.Cells[1]?.Value && formatCurrency(row.Cells[1].Value)}
            </td>
            <td className="px-6 py-3 text-sm text-right text-gray-900">
              {row.Cells[2]?.Value && formatCurrency(row.Cells[2].Value)}
            </td>
          </>
        ) : (
          <td className="px-6 py-3 text-sm text-right text-gray-900">
            {row.Cells[1]?.Value && formatCurrency(row.Cells[1].Value)}
          </td>
        )}
      </tr>
    );
  };

  const renderCashFlowAgingReport = (agingData) => {
    if (!agingData) return null;

    const { clientSummaries, totals, invoices, reportDate } = agingData;
    const clientList = Object.values(clientSummaries || {}).sort((a, b) => b.total - a.total);

    // Compute incoming cash buckets from not-yet-due invoices
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day7 = new Date(today); day7.setDate(today.getDate() + 7);
    const day14 = new Date(today); day14.setDate(today.getDate() + 14);
    const day21 = new Date(today); day21.setDate(today.getDate() + 21);

    const incomingIn7 = (invoices || [])
      .filter(inv => { const d = new Date(inv.dueDate); return d >= today && d <= day7; })
      .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
    const incomingIn14 = (invoices || [])
      .filter(inv => { const d = new Date(inv.dueDate); return d > day7 && d <= day14; })
      .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
    const incomingIn21 = (invoices || [])
      .filter(inv => { const d = new Date(inv.dueDate); return d > day14 && d <= day21; })
      .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
    const day30 = new Date(today); day30.setDate(today.getDate() + 30);
    const day45 = new Date(today); day45.setDate(today.getDate() + 45);
    const day60 = new Date(today); day60.setDate(today.getDate() + 60);
    const incomingIn30 = (invoices || [])
      .filter(inv => { const d = new Date(inv.dueDate); return d > day21 && d <= day30; })
      .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
    const incomingIn45 = (invoices || [])
      .filter(inv => { const d = new Date(inv.dueDate); return d > day30 && d <= day45; })
      .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);
    const incomingIn60 = (invoices || [])
      .filter(inv => { const d = new Date(inv.dueDate); return d > day45 && d <= day60; })
      .reduce((sum, inv) => sum + (inv.amountDue || 0), 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards — click to filter invoice details */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {[
            { key: 'aging-current', label: 'Current',    value: totals?.current || 0,   sub: 'Not yet due',       bg: 'bg-blue-50',   border: 'border-blue-200',   head: 'text-blue-600',   amt: 'text-blue-900',   ring: 'ring-blue-400' },
            { key: 'aging-1-30',    label: '1-30 Days',  value: totals?.days1To30 || 0,  sub: 'Recently overdue',  bg: 'bg-green-50',  border: 'border-green-200',  head: 'text-green-600',  amt: 'text-green-900',  ring: 'ring-green-400' },
            { key: 'aging-31-60',   label: '31-60 Days', value: totals?.days31To60 || 0, sub: 'Follow up needed',  bg: 'bg-yellow-50', border: 'border-yellow-200', head: 'text-yellow-600', amt: 'text-yellow-900', ring: 'ring-yellow-400' },
            { key: 'aging-61-90',   label: '61-90 Days', value: totals?.days61To90 || 0, sub: 'Seriously overdue', bg: 'bg-orange-50', border: 'border-orange-200', head: 'text-orange-600', amt: 'text-orange-900', ring: 'ring-orange-400' },
            { key: 'aging-90plus',  label: '90+ Days',   value: totals?.days90Plus || 0, sub: 'Urgent attention',  bg: 'bg-red-50',    border: 'border-red-200',    head: 'text-red-600',    amt: 'text-red-900',    ring: 'ring-red-400' },
          ].map(card => {
            const active = invoiceFilters.has(card.key);
            return (
              <div key={card.key}
                onClick={() => { const s = new Set(invoiceFilters); s.has(card.key) ? s.delete(card.key) : s.add(card.key); setInvoiceFilters(s); }}
                className={`${card.bg} rounded-lg p-4 border ${card.border} cursor-pointer transition-all select-none
                  ${active ? `ring-2 ${card.ring} shadow-md` : 'hover:shadow-sm opacity-90 hover:opacity-100'}`}>
                <h4 className={`text-sm font-medium ${card.head} mb-1`}>{card.label}</h4>
                <p className={`text-2xl font-bold ${card.amt}`}>{formatCurrency(card.value)}</p>
                <p className={`text-xs ${card.head} opacity-70`}>{active ? '✓ filtering' : card.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Incoming Cash Forecast — click to filter invoice details */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {[
            { key: 'due-7',  label: 'Due in 7 Days',  value: incomingIn7,  sub: 'Days 1–7',   bg: 'bg-teal-50',   border: 'border-teal-200',   head: 'text-teal-600',   amt: 'text-teal-900',   ring: 'ring-teal-400' },
            { key: 'due-14', label: 'Due in 14 Days', value: incomingIn14, sub: 'Days 8–14',  bg: 'bg-cyan-50',   border: 'border-cyan-200',   head: 'text-cyan-600',   amt: 'text-cyan-900',   ring: 'ring-cyan-400' },
            { key: 'due-21', label: 'Due in 21 Days', value: incomingIn21, sub: 'Days 15–21', bg: 'bg-sky-50',    border: 'border-sky-200',    head: 'text-sky-600',    amt: 'text-sky-900',    ring: 'ring-sky-400' },
            { key: 'due-30', label: 'Due in 30 Days', value: incomingIn30, sub: 'Days 22–30', bg: 'bg-indigo-50', border: 'border-indigo-200', head: 'text-indigo-600', amt: 'text-indigo-900', ring: 'ring-indigo-400' },
            { key: 'due-45', label: 'Due in 45 Days', value: incomingIn45, sub: 'Days 31–45', bg: 'bg-violet-50', border: 'border-violet-200', head: 'text-violet-600', amt: 'text-violet-900', ring: 'ring-violet-400' },
            { key: 'due-60', label: 'Due in 60 Days', value: incomingIn60, sub: 'Days 46–60', bg: 'bg-purple-50', border: 'border-purple-200', head: 'text-purple-600', amt: 'text-purple-900', ring: 'ring-purple-400' },
          ].map(card => {
            const active = invoiceFilters.has(card.key);
            return (
              <div key={card.key}
                onClick={() => { const s = new Set(invoiceFilters); s.has(card.key) ? s.delete(card.key) : s.add(card.key); setInvoiceFilters(s); }}
                className={`${card.bg} rounded-lg p-4 border ${card.border} cursor-pointer transition-all select-none
                  ${active ? `ring-2 ${card.ring} shadow-md` : 'hover:shadow-sm opacity-90 hover:opacity-100'}`}>
                <h4 className={`text-sm font-medium ${card.head} mb-1`}>{card.label}</h4>
                <p className={`text-2xl font-bold ${card.amt}`}>{formatCurrency(card.value)}</p>
                <p className={`text-xs ${card.head} opacity-70`}>{active ? '✓ filtering' : card.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Total Summary */}
        <div className="bg-white rounded-lg shadow border p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Total Outstanding Receivables</h3>
              <p className="text-sm text-gray-500">
                {totals?.totalInvoices || 0} invoices from {clientList.length} clients
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(totals?.total || 0)}</p>
              <p className="text-sm text-gray-500">As of {new Date(reportDate).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Client Aging Table */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Aging by Client</h3>
            <p className="text-sm text-gray-500">Receivables breakdown by customer</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    1-30 Days
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    31-60 Days
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    61-90 Days
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    90+ Days
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoices
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {clientList.map((client, index) => (
                  <tr key={client.contactId} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">
                      {client.contactName}
                    </td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">
                      {client.current > 0 ? formatCurrency(client.current) : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm text-right text-gray-900">
                      {client.days1To30 > 0 ? formatCurrency(client.days1To30) : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm text-right text-yellow-700 font-medium">
                      {client.days31To60 > 0 ? formatCurrency(client.days31To60) : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm text-right text-orange-700 font-medium">
                      {client.days61To90 > 0 ? formatCurrency(client.days61To90) : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm text-right text-red-700 font-bold">
                      {client.days90Plus > 0 ? formatCurrency(client.days90Plus) : '-'}
                    </td>
                    <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">
                      {formatCurrency(client.total)}
                    </td>
                    <td className="px-6 py-3 text-sm text-right text-gray-600">
                      {client.invoiceCount}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                  <td className="px-6 py-3 text-sm text-gray-900">TOTAL</td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(totals?.current || 0)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(totals?.days1To30 || 0)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(totals?.days31To60 || 0)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(totals?.days61To90 || 0)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(totals?.days90Plus || 0)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(totals?.total || 0)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-gray-900">
                    {totals?.totalInvoices || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Detailed Invoice List */}
        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Invoice Details</h3>
              <p className="text-sm text-gray-500">
                {invoiceFilters.size > 0
                  ? `Filtered by: ${[...invoiceFilters].join(', ')} — click boxes above to change`
                  : 'All outstanding invoices — click any box above to filter'}
              </p>
            </div>
            {invoiceFilters.size > 0 && (
              <button
                onClick={() => setInvoiceFilters(new Set())}
                className="text-sm text-gray-500 hover:text-gray-800 border border-gray-300 rounded px-3 py-1 hover:bg-gray-50">
                Clear filters ✕
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Overdue
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount Paid
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Aging
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(invoices || []).sort((a, b) => b.daysOverdue - a.daysOverdue).filter(invoice => {
                  if (invoiceFilters.size === 0) return true;
                  const due = invoice.dueDate ? new Date(invoice.dueDate) : null;
                  return [...invoiceFilters].some(f => {
                    if (f === 'aging-current') return invoice.agingBucket === 'Current';
                    if (f === 'aging-1-30')   return invoice.agingBucket === '1-30';
                    if (f === 'aging-31-60')  return invoice.agingBucket === '31-60';
                    if (f === 'aging-61-90')  return invoice.agingBucket === '61-90';
                    if (f === 'aging-90plus') return invoice.agingBucket === '90+';
                    if (!due) return false;
                    if (f === 'due-7')  return due >= today && due <= day7;
                    if (f === 'due-14') return due > day7  && due <= day14;
                    if (f === 'due-21') return due > day14 && due <= day21;
                    if (f === 'due-30') return due > day21 && due <= day30;
                    if (f === 'due-45') return due > day30 && due <= day45;
                    if (f === 'due-60') return due > day45 && due <= day60;
                    return false;
                  });
                }).map((invoice) => {
                  const agingColorClass = 
                    invoice.agingBucket === '90+' ? 'bg-red-100 text-red-800' :
                    invoice.agingBucket === '61-90' ? 'bg-orange-100 text-orange-800' :
                    invoice.agingBucket === '31-60' ? 'bg-yellow-100 text-yellow-800' :
                    invoice.agingBucket === '1-30' ? 'bg-green-100 text-green-800' :
                    'bg-blue-100 text-blue-800';

                  return (
                    <tr key={invoice.invoiceId} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm font-medium text-gray-900">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-3 text-sm text-gray-900">
                        {invoice.contactName}
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-600">
                        {invoice.date ? new Date(invoice.date).toLocaleDateString() : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-600">
                        {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}
                      </td>
                      <td className={`px-6 py-3 text-sm text-right font-medium ${
                        invoice.daysOverdue > 90 ? 'text-red-700' :
                        invoice.daysOverdue > 60 ? 'text-orange-700' :
                        invoice.daysOverdue > 30 ? 'text-yellow-700' :
                        invoice.daysOverdue > 0 ? 'text-green-700' :
                        'text-gray-600'
                      }`}>
                        {invoice.daysOverdue > 0 ? invoice.daysOverdue : '-'}
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900">
                        {formatCurrency(invoice.total)}
                      </td>
                      <td className="px-6 py-3 text-sm text-right text-gray-600">
                        {formatCurrency(invoice.amountPaid)}
                      </td>
                      <td className="px-6 py-3 text-sm text-right font-bold text-gray-900">
                        {formatCurrency(invoice.amountDue)}
                      </td>
                      <td className="px-6 py-3 text-sm">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${agingColorClass}`}>
                          {invoice.agingBucket === 'Current' ? 'Current' : `${invoice.agingBucket} days`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const currentReport = reports.find(r => r.id === activeReport);

  return (
    <div className="p-6 max-w-full mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Financial Reports</h1>
            <p className="text-gray-600 mt-1">View your Xero financial statements and reports</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadReport}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={async () => {
                try {
                  await ApiService.clearXeroReportsCache();
                  console.log('🗑️ Cleared server-side report cache');
                  alert('Server cache cleared successfully. Reports will be refreshed from Xero on next load.');
                  // Reload current report
                  loadReport();
                } catch (error) {
                  console.error('Error clearing cache:', error);
                  alert('Failed to clear cache: ' + error.message);
                }
              }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
              title="Clear server-side cached report data"
            >
              🗑️ Clear Cache
            </button>
            <button
              onClick={() => window.print()}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
          </div>
        </div>

        {/* Report Type Selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {reports.map((report) => {
            const Icon = report.icon;
            return (
              <button
                key={report.id}
                onClick={() => setActiveReport(report.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeReport === report.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {report.name}
              </button>
            );
          })}
        </div>

        {/* Date Controls — only shown when connected to Xero */}
        {isConnected && (
          <div className="bg-white rounded-lg shadow border p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">
              {activeReport === 'profit-loss' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                    <input
                      type="date"
                      value={dateRange.fromDate}
                      onChange={(e) => handleDateChange('fromDate', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                    <input
                      type="date"
                      value={dateRange.toDate}
                      onChange={(e) => handleDateChange('toDate', e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">As of Date</label>
                  <input
                    type="date"
                    value={dateRange.asOfDate}
                    onChange={(e) => handleDateChange('asOfDate', e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
              <div className="text-sm text-gray-600">
                <strong>{currentReport?.name}</strong>: {currentReport?.description}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error/Cache Message */}
      {error && (
        <div className={`border rounded-lg p-4 mb-6 ${
          error.includes('cached data') 
            ? 'bg-yellow-50 border-yellow-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center">
            {error.includes('cached data') ? (
              <CheckCircle className="w-5 h-5 text-yellow-500 mr-2" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            )}
            <div>
              <p className={`text-sm ${
                error.includes('cached data') ? 'text-yellow-800' : 'text-red-800'
              }`}>
                <strong>
                  {error.includes('cached data') ? 'Using Cached Data:' : 'Error loading report:'}
                </strong> {error}
              </p>
              <p className={`text-xs mt-1 ${
                error.includes('cached data') ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {error.includes('cached data') 
                  ? 'This is your last known data. Reconnect to Xero for live updates.'
                  : 'Showing sample data for demonstration. Connect to Xero to see real data.'
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Executive Summary Boxes - Only for Profit & Loss */}
      {activeReport === 'profit-loss' && summaryMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Revenue</p>
                <p className="text-2xl font-bold text-blue-900">{formatCurrency(summaryMetrics.totalRevenue)}</p>
                <p className="text-xs text-blue-500">100% of income</p>
              </div>
              <DollarSign className="w-8 h-8 text-blue-500" />
            </div>
            {currentYearQuarters.length > 0 && (
              <div className="mt-3 pt-3 border-t border-blue-200 space-y-1">
                {currentYearQuarters.map(q => (
                  <div key={q.name} className="flex justify-between text-xs">
                    <span className="text-blue-600">{q.name}{q.isPartial ? '*' : ''}</span>
                    <span className="font-medium text-blue-800">{formatCurrency(q.totalRevenue)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Core Operating Expenses</p>
                <p className="text-2xl font-bold text-red-900">{formatCurrency(-summaryMetrics.coreOperatingExpenses)}</p>
                <p className="text-xs text-red-500">{((summaryMetrics.coreOperatingExpenses / summaryMetrics.totalRevenue) * 100).toFixed(1)}% of revenue</p>
              </div>
              <TrendingUp className="w-8 h-8 text-red-500 rotate-180" />
            </div>
            {currentYearQuarters.length > 0 && (
              <div className="mt-3 pt-3 border-t border-red-200 space-y-1">
                {currentYearQuarters.map(q => (
                  <div key={q.name} className="flex justify-between text-xs">
                    <span className="text-red-600">{q.name}{q.isPartial ? '*' : ''}</span>
                    <span className="font-medium text-red-800">{formatCurrency(-q.coreOperatingExpenses)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Core Profit</p>
                <p className="text-2xl font-bold text-green-900">{formatCurrency(summaryMetrics.coreProfit)}</p>
                <p className="text-xs text-green-500">{summaryMetrics.coreMargin.toFixed(1)}% margin</p>
              </div>
              <BarChart3 className="w-8 h-8 text-green-500" />
            </div>
            {currentYearQuarters.length > 0 && (
              <div className="mt-3 pt-3 border-t border-green-200 space-y-1">
                {currentYearQuarters.map(q => (
                  <div key={q.name} className="flex justify-between text-xs">
                    <span className="text-green-600">{q.name}{q.isPartial ? '*' : ''}</span>
                    <span className="font-medium text-green-800">{formatCurrency(q.coreProfit)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 font-medium">Net Profit (YTD)</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(summaryMetrics.realProfit)}</p>
                <p className="text-xs text-gray-500">{summaryMetrics.realMargin.toFixed(1)}% margin • Year-to-date</p>
              </div>
              <FileText className="w-8 h-8 text-gray-500" />
            </div>
            {currentYearQuarters.length > 0 && (
              <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
                {currentYearQuarters.map(q => (
                  <div key={q.name} className="flex justify-between text-xs">
                    <span className="text-gray-600">{q.name}{q.isPartial ? '*' : ''}</span>
                    <span className="font-medium text-gray-800">{formatCurrency(q.realProfit)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* AB Consulting Impact Banner */}
      {activeReport === 'profit-loss' && summaryMetrics && summaryMetrics.abConsultingAmount !== 0 && (
        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="text-2xl">💼</div>
              <div>
                <h3 className="font-semibold text-purple-800">AB Consulting Impact</h3>
                <p className="text-purple-700">
                  External consulting reduces profit by {formatCurrency(Math.abs(summaryMetrics.abConsultingAmount))} 
                  ({summaryMetrics.abConsultingImpact.toFixed(1)}% of revenue)
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-purple-900">{formatCurrency(summaryMetrics.abConsultingAmount)}</p>
              <p className="text-xs text-purple-600">External Cost</p>
            </div>
          </div>
        </div>
      )}

      {/* Business Insights Section - Only for Profit & Loss */}
      {activeReport === 'profit-loss' && summaryMetrics && (
        <div className="bg-white rounded-lg shadow border p-6 mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Business Insights & Projections
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                {priorYearData && priorYearData.quarters && priorYearData.quarters.length === 4
                  ? `📊 Quarterly Projection: Using Q1-Q${Math.floor(new Date().getMonth() / 3) + 1} ${new Date().getFullYear()} actual data + prior year quarterly patterns for remaining quarters`
                  : priorYearData
                    ? '📊 Year-over-Year Projection: Using growth rate from same period last year'
                    : '📊 Linear Projection: Extrapolating based on current year performance'
                }
              </p>
            </div>
            <button
              onClick={() => {
                const currentYear = new Date().getFullYear();
                const priorYear = currentYear - 1;
                const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
                
                let explanation = '';
                
                if (priorYearData && priorYearData.quarters && priorYearData.quarters.length === 4) {
                  // Quarterly projection explanation
                  const remainingQuarters = 4 - currentQuarter;
                  const growthRate = priorYearData.samePeriod?.totalRevenue > 0 
                    ? ((summaryMetrics.totalRevenue - priorYearData.samePeriod.totalRevenue) / priorYearData.samePeriod.totalRevenue * 100)
                    : 0;
                  
                  explanation = `Year-End Forecast Methodology (Quarterly Projection - MOST ACCURATE):

📊 STEP 1: Gather Prior Year Quarterly Data
${priorYearData.quarters.map(q => `  ${q.quarter} ${priorYear}: ${formatCurrency(q.totalRevenue)}`).join('\n')}
  Total ${priorYear}: ${formatCurrency(priorYearData.fullYear)}

📈 STEP 2: Calculate Current Year Growth Rate
  Jan 1 - ${new Date().toLocaleDateString()} ${currentYear}: ${formatCurrency(summaryMetrics.totalRevenue)}
  Same Period ${priorYear}: ${formatCurrency(priorYearData.samePeriod.totalRevenue)}
  YoY Growth Rate: ${growthRate > 0 ? '+' : ''}${growthRate.toFixed(1)}%

🎯 STEP 3: Project Remaining Quarters
  Current Period Actual: ${formatCurrency(summaryMetrics.totalRevenue)}
${remainingQuarters > 0 ? `  ${remainingQuarters} Remaining Quarter(s): Apply Q${currentQuarter + 1}-Q4 ${priorYear} revenue × (1 + ${growthRate.toFixed(1)}%)` : '  ✅ All quarters complete'}

💰 STEP 4: Calculate Projected Year-End
  Projected ${currentYear} Revenue = Current Actual + Projected Remaining Quarters
  Projected ${currentYear} Profit = Projected Revenue × Current Margin (${summaryMetrics.coreMargin.toFixed(1)}%)

✅ This method accounts for:
  • Seasonal variations (Q4 typically stronger/weaker)
  • Growth trends (maintaining ${growthRate.toFixed(1)}% growth)
  • Actual performance patterns from ${priorYear}`;
                  
                } else if (priorYearData && priorYearData.samePeriod) {
                  // YoY comparison explanation
                  const growthRate = ((summaryMetrics.totalRevenue - priorYearData.samePeriod.totalRevenue) / priorYearData.samePeriod.totalRevenue * 100);
                  
                  explanation = `Year-End Forecast Methodology (Year-over-Year):

1. Compare current period to same period last year
2. Calculate YoY growth rate: ${growthRate.toFixed(1)}%
3. Estimate last year's full-year revenue
4. Apply growth rate to project this year
5. Apply current margin (${summaryMetrics.coreMargin.toFixed(1)}%)

Data:
- ${priorYear} (same period): ${formatCurrency(priorYearData.samePeriod.totalRevenue)}
- ${currentYear} (same period): ${formatCurrency(summaryMetrics.totalRevenue)}
- Growth: ${growthRate > 0 ? '+' : ''}${growthRate.toFixed(1)}%`;
                  
                } else {
                  // Linear projection
                  const daysPassed = Math.floor((new Date() - new Date(currentYear, 0, 1)) / (1000 * 60 * 60 * 24));
                  explanation = `Year-End Forecast Methodology (Linear):

1. Days passed: ${daysPassed}
2. Daily revenue: ${formatCurrency(summaryMetrics.totalRevenue / daysPassed)}
3. Project remaining days
4. Apply current margin

Note: Prior year data not available.`;
                }
                
                alert(explanation);
              }}
              className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1 rounded-lg transition-colors"
            >
              ℹ️ How is this calculated?
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Year-End Forecast with Prior Year Comparison */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start justify-between mb-2">
                <div className="text-2xl">📊</div>
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <h4 className="font-semibold text-blue-800 mb-2">Year-End Forecast</h4>
              {(() => {
                const currentMonth = new Date().getMonth(); // 0-11
                const currentQuarter = Math.floor(currentMonth / 3) + 1; // 1-4
                
                let projectedYearEnd, projectedProfit, growthRate = null;
                let projectionMethod = 'linear';
                let revenueGrowthRate = 0;
                let profitGrowthRate = 0;
                
                // Use quarterly projection if we have prior year data
                if (priorYearData && priorYearData.quarters && priorYearData.quarters.length === 4) {
                  projectionMethod = 'quarterly';
                  
                  // Current year revenue so far
                  const currentYearSoFar = summaryMetrics.totalRevenue;
                  
                  // Prior year same period
                  const priorYearSamePeriod = priorYearData.samePeriod?.totalRevenue || 0;
                  
                  // Calculate YoY growth rates for completed period
                  revenueGrowthRate = priorYearSamePeriod > 0 
                    ? ((currentYearSoFar - priorYearSamePeriod) / priorYearSamePeriod) * 100 
                    : 0;
                  
                  profitGrowthRate = priorYearData.samePeriod?.coreProfit 
                    ? ((summaryMetrics.coreProfit - priorYearData.samePeriod.coreProfit) / Math.abs(priorYearData.samePeriod.coreProfit)) * 100
                    : 0;
                  
                  growthRate = revenueGrowthRate; // For display
                  
                  // Get current date within quarter
                  const currentDate = new Date();
                  const quarterStartMonth = (currentQuarter - 1) * 3;
                  const quarterEndMonth = quarterStartMonth + 2;
                  const quarterStart = new Date(currentDate.getFullYear(), quarterStartMonth, 1);
                  const quarterEnd = new Date(currentDate.getFullYear(), quarterEndMonth + 1, 0); // Last day of quarter
                  const daysInCurrentQuarter = Math.floor((quarterEnd - quarterStart) / (1000 * 60 * 60 * 24));
                  const daysPassedInQuarter = Math.floor((currentDate - quarterStart) / (1000 * 60 * 60 * 24));
                  const percentQuarterComplete = daysPassedInQuarter / daysInCurrentQuarter;
                  
                  // Find prior year current quarter for projection
                  const priorCurrentQuarter = priorYearData.quarters.find(pq => pq.quarter === `Q${currentQuarter}`);

                  // Prefer actual current-quarter data over prior-year estimate
                  const actualCurrentQData = currentYearQuarters.find(q => q.name === `Q${currentQuarter}`);

                  // Project rest of current quarter
                  let projectedRestOfCurrentQuarter = 0;
                  if (percentQuarterComplete < 1) {
                    if (actualCurrentQData && daysPassedInQuarter > 0) {
                      // Use real daily rate from actual Q revenue so far
                      const dailyRateActual = actualCurrentQData.totalRevenue / daysPassedInQuarter;
                      projectedRestOfCurrentQuarter = dailyRateActual * (daysInCurrentQuarter - daysPassedInQuarter);
                    } else if (priorCurrentQuarter) {
                      // Fall back to prior year pattern when no actual data yet
                      const projectedFullQuarter = priorCurrentQuarter.totalRevenue * (1 + (growthRate / 100));
                      projectedRestOfCurrentQuarter = projectedFullQuarter * (1 - percentQuarterComplete);
                    }
                  }
                  
                  // Start with actual current year revenue + rest of current quarter
                  projectedYearEnd = currentYearSoFar + projectedRestOfCurrentQuarter;
                  
                  // Add projected future quarters using prior year data + growth rate
                  for (let q = currentQuarter + 1; q <= 4; q++) {
                    const priorQuarter = priorYearData.quarters.find(pq => pq.quarter === `Q${q}`);
                    if (priorQuarter) {
                      const projectedQuarter = priorQuarter.totalRevenue * (1 + (growthRate / 100));
                      projectedYearEnd += projectedQuarter;
                    }
                  }
                  
                  // Project profit using variable expense margin + one flat tax payment per quarter.
                  // Variable margin = revenue minus expenses-excluding-tax (scales with revenue).
                  // Tax is treated as a lump-sum once per quarter, never prorated daily.
                  const variableExpenseMargin = summaryMetrics.totalRevenue > 0
                    ? summaryMetrics.coreOperatingExpensesExTax / summaryMetrics.totalRevenue
                    : 0;

                  // Tax already paid YTD (actual).
                  let projectedTax = summaryMetrics.incomeTaxExpense;

                  // Current quarter: if tax hasn't been paid yet this quarter, budget one payment.
                  const actualCQTax = actualCurrentQData?.incomeTaxExpense ?? 0;
                  if (actualCQTax === 0 && priorCurrentQuarter?.incomeTaxExpense > 0) {
                    projectedTax += priorCurrentQuarter.incomeTaxExpense * (1 + growthRate / 100);
                  }

                  // Future quarters: one tax payment each, scaled by YoY growth.
                  for (let q = currentQuarter + 1; q <= 4; q++) {
                    const pq = priorYearData.quarters.find(pq => pq.quarter === `Q${q}`);
                    if (pq?.incomeTaxExpense > 0) {
                      projectedTax += pq.incomeTaxExpense * (1 + growthRate / 100);
                    }
                  }

                  projectedProfit = projectedYearEnd * (1 - variableExpenseMargin) - projectedTax;
                  
                } else if (priorYearData && priorYearData.samePeriod) {
                  // Use simple YoY comparison if no quarterly data
                  projectionMethod = 'yoy';
                  growthRate = ((summaryMetrics.totalRevenue - priorYearData.samePeriod.totalRevenue) / priorYearData.samePeriod.totalRevenue) * 100;
                  
                  const priorYearFull = priorYearData.fullYear || (priorYearData.samePeriod.totalRevenue * (365 / Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24))));
                  projectedYearEnd = priorYearFull * (1 + (growthRate / 100));
                  
                  const currentMargin = summaryMetrics.coreProfit / summaryMetrics.totalRevenue;
                  projectedProfit = projectedYearEnd * currentMargin;
                } else {
                  // Fallback to simple linear projection
                  const daysPassed = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24));
                  const daysInYear = 365;
                  const daysRemaining = daysInYear - daysPassed;
                  const dailyRevenue = summaryMetrics.totalRevenue / daysPassed;
                  projectedYearEnd = summaryMetrics.totalRevenue + (dailyRevenue * daysRemaining);
                  projectedProfit = summaryMetrics.coreProfit * (projectedYearEnd / summaryMetrics.totalRevenue);
                }
                
                // Calculate projected AB Consulting costs
                // AB Consulting is paid at END of each quarter = 33% of that quarter's core profit
                let projectedAbConsulting = 0;
                
                if (projectionMethod === 'quarterly' && priorYearData?.quarters) {
                  // Start with actual AB Consulting paid YTD (for completed quarters)
                  const currentYearAbConsulting = Math.abs(summaryMetrics.abConsultingAmount);
                  
                  // For current quarter (incomplete), project 33% of full quarter's core profit
                  const currentQuarterPriorData = priorYearData.quarters.find(pq => pq.quarter === `Q${currentQuarter}`);
                  let currentQuarterAbConsulting = 0;
                  if (currentQuarterPriorData) {
                    const projectedCurrentQuarterRevenue = currentQuarterPriorData.totalRevenue * (1 + (growthRate / 100));
                    const projectedCurrentQuarterProfit = projectedCurrentQuarterRevenue * (summaryMetrics.coreProfit / summaryMetrics.totalRevenue);
                    currentQuarterAbConsulting = projectedCurrentQuarterProfit * 0.33;
                  }
                  
                  // For future quarters (after current), project 33% of each quarter's core profit
                  let futureQuartersAbConsulting = 0;
                  for (let q = currentQuarter + 1; q <= 4; q++) {
                    const priorQuarter = priorYearData.quarters.find(pq => pq.quarter === `Q${q}`);
                    if (priorQuarter) {
                      const projectedQuarterRevenue = priorQuarter.totalRevenue * (1 + (growthRate / 100));
                      const projectedQuarterProfit = projectedQuarterRevenue * (summaryMetrics.coreProfit / summaryMetrics.totalRevenue);
                      futureQuartersAbConsulting += projectedQuarterProfit * 0.33;
                    }
                  }
                  
                  // Total AB Consulting = YTD actual + current quarter projection + future quarters
                  projectedAbConsulting = currentYearAbConsulting + currentQuarterAbConsulting + futureQuartersAbConsulting;
                  
                } else {
                  // Fallback: use 33% of projected core profit
                  projectedAbConsulting = projectedProfit * 0.33;
                }
                
                const projectedNetProfit = projectedProfit - projectedAbConsulting;

                // Always compute linear values for comparison
                const daysPassed = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24));
                const linearRevenue = summaryMetrics.totalRevenue * (365 / daysPassed);
                const currentMargin = summaryMetrics.coreProfit / summaryMetrics.totalRevenue;
                const linearCoreProfit = linearRevenue * currentMargin;
                const linearAbConsulting = linearCoreProfit * 0.33;
                const linearNetProfit = linearCoreProfit - linearAbConsulting;

                // Quarterly values only available when projectionMethod === 'quarterly'
                const hasQuarterly = projectionMethod === 'quarterly';

                return (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-blue-700">Projected Revenue:</span>
                      <span className="font-bold text-blue-900">{formatCurrency(projectedYearEnd)}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-blue-200 pt-1 mt-1">
                      <span className="text-blue-700">Projected Core Profit:</span>
                      <span className="font-bold text-green-700">{formatCurrency(projectedProfit)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-xs">
                      <span className="text-blue-600 italic">Less: AB Consulting (33% of core profit):</span>
                      <span className="text-purple-600 italic">-{formatCurrency(projectedAbConsulting)}</span>
                    </div>
                    <div className="border-t border-blue-300 pt-1 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">📈 Linear Net Profit:</span>
                        <span className="font-bold text-green-700">{formatCurrency(linearNetProfit)}</span>
                      </div>
                      {hasQuarterly && (
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-800 font-semibold">📊 Quarterly Net Profit:</span>
                          <span className="font-bold text-green-800">{formatCurrency(projectedNetProfit)}</span>
                        </div>
                      )}
                    </div>
                    {hasQuarterly && (
                      <>
                        <div className="flex justify-between text-sm border-t border-blue-200 pt-1 mt-1">
                          <span className="text-blue-600 text-xs">Revenue Growth YoY:</span>
                          <span className={`font-semibold text-xs ${revenueGrowthRate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {revenueGrowthRate > 0 ? '+' : ''}{revenueGrowthRate.toFixed(1)}%
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-blue-600 text-xs">Profit Growth YoY:</span>
                          <span className={`font-semibold text-xs ${profitGrowthRate > 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {profitGrowthRate > 0 ? '+' : ''}{profitGrowthRate.toFixed(1)}%
                          </span>
                        </div>
                      </>
                    )}
                    {growthRate !== null && !hasQuarterly && (
                      <div className="flex justify-between text-sm">
                        <span className="text-blue-700">Revenue Growth:</span>
                        <span className={`font-bold ${growthRate > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {growthRate > 0 ? '+' : ''}{growthRate.toFixed(1)}%
                        </span>
                      </div>
                    )}
                    <div className="text-xs text-blue-600 mt-2 border-t border-blue-200 pt-1">
                      {hasQuarterly ? '✅ Quarterly projection (most accurate)' :
                       projectionMethod === 'yoy' ? '📊 Year-over-year projection' :
                       '📈 Linear extrapolation'}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Monthly Burn Rate */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
              <div className="flex items-start justify-between mb-2">
                <div className="text-2xl">💸</div>
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <h4 className="font-semibold text-orange-800 mb-2">Monthly Burn Rate</h4>
              {(() => {
                const monthsPassed = (new Date().getMonth() + 1) + (new Date().getDate() / 30);
                const avgMonthlyExpenses = Math.abs(summaryMetrics.coreOperatingExpenses) / monthsPassed;
                const avgMonthlyRevenue = summaryMetrics.totalRevenue / monthsPassed;
                const avgMonthlyProfit = avgMonthlyRevenue - avgMonthlyExpenses;
                
                return (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-700">Avg Monthly Revenue:</span>
                      <span className="font-bold text-orange-900">{formatCurrency(avgMonthlyRevenue)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-700">Avg Monthly Expenses:</span>
                      <span className="font-bold text-red-700">{formatCurrency(avgMonthlyExpenses)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-orange-700">Avg Monthly Profit:</span>
                      <span className="font-bold text-green-700">{formatCurrency(avgMonthlyProfit)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Current Quarter Projection */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
              <div className="flex items-start justify-between mb-2">
                <div className="text-2xl">📅</div>
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <h4 className="font-semibold text-purple-800 mb-2">Current Quarter (Q{Math.floor(new Date().getMonth() / 3) + 1}) Projection</h4>
              {(() => {
                const currentMonth = new Date().getMonth();
                const currentQuarter = Math.floor(currentMonth / 3) + 1;
                const quarterStartMonth = (currentQuarter - 1) * 3;
                const quarterEndMonth = quarterStartMonth + 2;
                const quarterStart = new Date(new Date().getFullYear(), quarterStartMonth, 1);
                const quarterEnd = new Date(new Date().getFullYear(), quarterEndMonth + 1, 0);
                
                // Calculate how far we are into the quarter
                const currentDate = new Date();
                const daysInQuarter = Math.floor((quarterEnd - quarterStart) / (1000 * 60 * 60 * 24));
                const daysPassedInQuarter = Math.floor((currentDate - quarterStart) / (1000 * 60 * 60 * 24));
                const percentComplete = Math.min((daysPassedInQuarter / daysInQuarter) * 100, 100);
                
                // Calculate YTD revenue and profit for current quarter
                const quarterStartDate = `${new Date().getFullYear()}-${String(quarterStartMonth + 1).padStart(2, '0')}-01`;
                const today = new Date().toISOString().split('T')[0];
                
                // Use actual current quarter data when available
                const actualQData = currentYearQuarters.find(q => q.name === `Q${currentQuarter}`);
                const actualQRevenue = actualQData?.totalRevenue || 0;
                const actualQProfit = actualQData?.coreProfit || 0;

                // Linear projection: actual revenue so far + (daily rate × remaining days)
                // When 100% complete this equals the actual quarter revenue exactly.
                const remainingDaysInQ = Math.max(0, daysInQuarter - daysPassedInQuarter);
                const dailyRateQ = actualQRevenue > 0 && daysPassedInQuarter > 0
                  ? actualQRevenue / daysPassedInQuarter
                  : summaryMetrics.totalRevenue / Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (1000 * 60 * 60 * 24));
                const linearQuarterRevenue = actualQRevenue + (dailyRateQ * remainingDaysInQ);
                const linearQuarterProfit = actualQProfit > 0 && daysPassedInQuarter > 0
                  ? (actualQProfit / daysPassedInQuarter) * daysInQuarter
                  : linearQuarterRevenue * (summaryMetrics.coreProfit / summaryMetrics.totalRevenue);

                // Quarterly projection: if quarter is complete use actual; otherwise prior year × growth
                let quarterlyProjection = null;
                if (priorYearData?.quarters) {
                  const priorQuarter = priorYearData.quarters.find(q => q.quarter === `Q${currentQuarter}`);
                  if (priorQuarter) {
                    if (percentComplete >= 100 && actualQRevenue > 0) {
                      // Quarter is done — show actual, not an estimate
                      quarterlyProjection = actualQRevenue;
                    } else {
                      const revenueGrowth = priorYearData.samePeriod?.totalRevenue > 0
                        ? ((summaryMetrics.totalRevenue - priorYearData.samePeriod.totalRevenue) / priorYearData.samePeriod.totalRevenue)
                        : 0;
                      quarterlyProjection = priorQuarter.totalRevenue * (1 + revenueGrowth);
                    }
                  }
                }
                
                return (
                  <div className="space-y-2">
                    <div className="flex justify-between text-xs text-purple-600 mb-1">
                      <span>{percentComplete.toFixed(0)}% complete</span>
                      <span>{daysPassedInQuarter}/{daysInQuarter} days</span>
                    </div>
                    
                    <div className="flex justify-between text-sm border-t border-purple-200 pt-1">
                      <span className="text-purple-700">📈 Linear Method:</span>
                      <span className="font-bold text-purple-900">{formatCurrency(linearQuarterRevenue)}</span>
                    </div>
                    
                    {quarterlyProjection && (
                      <div className="flex justify-between text-sm">
                        <span className="text-purple-700">📊 Quarterly Method:</span>
                        <span className="font-bold text-purple-900">{formatCurrency(quarterlyProjection)}</span>
                      </div>
                    )}
                    
                    <div className="border-t border-purple-200 pt-1 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-purple-700">📈 Linear Profit:</span>
                        <span className="font-bold text-green-700">{formatCurrency(linearQuarterProfit)}</span>
                      </div>
                      {quarterlyProjection && (
                        <div className="flex justify-between text-sm">
                          <span className="text-purple-800 font-semibold">📊 Quarterly Profit:</span>
                          <span className="font-bold text-green-800">
                            {formatCurrency(
                              percentComplete >= 100 && actualQProfit > 0
                                ? actualQProfit
                                : quarterlyProjection * (summaryMetrics.coreProfit / summaryMetrics.totalRevenue)
                            )}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="text-xs text-purple-600 mt-1">
                      {quarterlyProjection ? '✅ Using quarterly pattern' : '📈 Using linear trend'}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Profit Margin Trend */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
              <div className="flex items-start justify-between mb-2">
            <div className="text-2xl">📈</div>
                <TrendingUp className="w-5 h-5 text-green-600" />
            </div>
              <h4 className="font-semibold text-green-800 mb-2">Margin Performance</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-green-700">Current Margin:</span>
                  <span className="font-bold text-green-900">{summaryMetrics.coreMargin.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-green-700">Industry Avg:</span>
                  <span className="font-bold text-gray-700">20-25%</span>
                </div>
                <div className="text-xs text-green-600 mt-2">
                  {summaryMetrics.coreMargin > 25 ? `✅ Above average` : summaryMetrics.coreMargin > 20 ? `✓ Good` : `⚠️ Below average`}
                </div>
              </div>
            </div>

            {/* Year-over-Year Comparison */}
            {priorYearData && priorYearData.samePeriod && (
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-4 border border-yellow-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="text-2xl">📅</div>
                  <TrendingUp className="w-5 h-5 text-yellow-600" />
                </div>
                <h4 className="font-semibold text-yellow-800 mb-2">vs. Same Period Last Year</h4>
                {(() => {
                  const revenueGrowth = ((summaryMetrics.totalRevenue - priorYearData.samePeriod.totalRevenue) / priorYearData.samePeriod.totalRevenue) * 100;
                  const profitGrowth = ((summaryMetrics.coreProfit - priorYearData.samePeriod.coreProfit) / Math.abs(priorYearData.samePeriod.coreProfit)) * 100;
                  const marginChange = summaryMetrics.coreMargin - priorYearData.samePeriod.coreMargin;
                  
                  return (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-yellow-700">Revenue Growth:</span>
                        <span className={`font-bold ${revenueGrowth > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {revenueGrowth > 0 ? '+' : ''}{revenueGrowth.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-yellow-700">Profit Growth:</span>
                        <span className={`font-bold ${profitGrowth > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {profitGrowth > 0 ? '+' : ''}{profitGrowth.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-yellow-700">Margin Change:</span>
                        <span className={`font-bold ${marginChange > 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {marginChange > 0 ? '+' : ''}{marginChange.toFixed(1)}pp
                        </span>
                      </div>
                      <div className="text-xs text-yellow-600 mt-2">
                        Last Year: {formatCurrency(priorYearData.samePeriod.totalRevenue)}
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Cash Flow Status */}
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-4 border border-teal-200">
              <div className="flex items-start justify-between mb-2">
                <div className="text-2xl">💰</div>
                <DollarSign className="w-5 h-5 text-teal-600" />
              </div>
              <h4 className="font-semibold text-teal-800 mb-2">Financial Health</h4>
              {(() => {
                const profitRatio = summaryMetrics.coreProfit / summaryMetrics.totalRevenue;
                const healthScore = profitRatio > 0.25 ? 'Excellent' : profitRatio > 0.20 ? 'Good' : profitRatio > 0.15 ? 'Fair' : 'Needs Attention';
                const healthColor = profitRatio > 0.25 ? 'text-green-700' : profitRatio > 0.20 ? 'text-blue-700' : profitRatio > 0.15 ? 'text-yellow-700' : 'text-red-700';
                
                return (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-teal-700">Health Score:</span>
                      <span className={`font-bold ${healthColor}`}>{healthScore}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-teal-700">Profit Ratio:</span>
                      <span className="font-bold text-teal-900">{(profitRatio * 100).toFixed(1)}%</span>
                    </div>
                    <div className="text-xs text-teal-600 mt-2">
                      Based on revenue vs profit analysis
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading {currentReport?.name}...</span>
        </div>
      ) : (
        /* Report Content */
        activeReport === 'cash-flow-aging' ? (
          reportData && renderCashFlowAgingReport(reportData)
        ) : (
          reportData?.Reports?.[0] && renderReportTable(reportData.Reports[0])
        )
      )}
    </div>
  );
};

export default FinancialReports;
